import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  DeliverableCategory,
  Priority,
  ProofType,
  Stage,
  CreateTemplateData,
} from '../../types/deliverable';
import { ChecklistBuilder, ChecklistItemData } from './ChecklistBuilder';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTemplateData) => Promise<void>;
  initialData?: Partial<CreateTemplateData>;
}

const categories: DeliverableCategory[] = [
  'PROPOSAL',
  'CONTRACT',
  'IMPLEMENTATION',
  'INSTALLATION',
  'TRAINING',
  'SUPPORT',
  'DOCUMENTATION',
  'LICENSE',
  'SERVICE',
  'EQUIPMENT',
  'CONSULTATION',
  'MAINTENANCE',
  'OTHER',
];

const priorities: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const proofTypes: ProofType[] = [
  'PHOTO',
  'VIDEO',
  'DOCUMENT',
  'SIGNATURE',
  'SCREENSHOT',
  'OTHER',
];

const stages: Stage[] = [
  'NEW',
  'CONTACTED',
  'DISCUSSION',
  'PROPOSAL',
  'NEGOTIATION',
  'WON',
  'LOST',
];

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [formData, setFormData] = useState<CreateTemplateData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: initialData?.category || 'OTHER',
    priority: initialData?.priority || 'MEDIUM',
    defaultDuration: initialData?.defaultDuration || 7,
    defaultTitle: initialData?.defaultTitle || '',
    defaultDescription: initialData?.defaultDescription || '',
    proofRequired: initialData?.proofRequired ?? true,
    requiredProofTypes: initialData?.requiredProofTypes || [],
    minProofCount: initialData?.minProofCount || 1,
    isPublic: initialData?.isPublic ?? true,
    applyOnStageChange: initialData?.applyOnStageChange ?? false,
    targetStage: initialData?.targetStage,
    checklistItems: initialData?.checklistItems || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChecklistChange = (items: ChecklistItemData[]) => {
    setFormData((prev) => ({
      ...prev,
      checklistItems: items.map((item) => ({
        title: item.title,
        description: item.description,
        sortOrder: item.sortOrder,
        isRequired: item.isRequired,
        requiresProof: item.requiresProof,
      })),
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = 'Template name must be at least 3 characters';
    }

    if (formData.name.length > 200) {
      newErrors.name = 'Template name must be less than 200 characters';
    }

    if (formData.defaultDuration < 1 || formData.defaultDuration > 365) {
      newErrors.defaultDuration = 'Duration must be between 1 and 365 days';
    }

    if (!formData.defaultTitle || formData.defaultTitle.trim().length < 3) {
      newErrors.defaultTitle = 'Default title must be at least 3 characters';
    }

    if (formData.checklistItems.length === 0) {
      newErrors.checklist = 'At least one checklist item is required';
    }

    if (formData.checklistItems.length > 50) {
      newErrors.checklist = 'Maximum 50 checklist items allowed';
    }

    if (formData.proofRequired && formData.minProofCount < 0) {
      newErrors.minProofCount = 'Minimum proof count cannot be negative';
    }

    if (formData.applyOnStageChange && !formData.targetStage) {
      newErrors.targetStage = 'Target stage is required when auto-apply is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      setSubmitError(message);
      console.error('Error creating template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProofTypeToggle = (type: ProofType) => {
    setFormData((prev) => ({
      ...prev,
      requiredProofTypes: prev.requiredProofTypes.includes(type)
        ? prev.requiredProofTypes.filter((t) => t !== type)
        : [...prev.requiredProofTypes, type],
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {initialData ? 'Edit Template' : 'Create Deliverable Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Submit Error */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Standard Gym Equipment Installation"
                required
                maxLength={200}
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Brief description of this template"
                rows={3}
                maxLength={1000}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value as DeliverableCategory,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority *
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: e.target.value as Priority,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {priorities.map((pri) => (
                    <option key={pri} value={pri}>
                      {pri}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Duration (days) *
                </label>
                <Input
                  type="number"
                  value={formData.defaultDuration}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      defaultDuration: parseInt(e.target.value) || 1,
                    }))
                  }
                  min={1}
                  max={365}
                  required
                />
                {errors.defaultDuration && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.defaultDuration}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Title *
              </label>
              <Input
                value={formData.defaultTitle}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    defaultTitle: e.target.value,
                  }))
                }
                placeholder="e.g., {{leadName}} - Equipment Installation"
                required
                maxLength={500}
              />
              {errors.defaultTitle && (
                <p className="text-sm text-red-600 mt-1">{errors.defaultTitle}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Use {"{{leadName}}"} or {"{{company}}"} for dynamic values
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Description
              </label>
              <Textarea
                value={formData.defaultDescription}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    defaultDescription: e.target.value,
                  }))
                }
                placeholder="Default description for deliverables created from this template"
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          {/* Checklist Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Checklist Items</h3>
            <ChecklistBuilder
              items={formData.checklistItems.map((item, index) => ({
                id: `item-${index}`,
                ...item,
              }))}
              onChange={handleChecklistChange}
              maxItems={50}
            />
            {errors.checklist && (
              <p className="text-sm text-red-600">{errors.checklist}</p>
            )}
          </div>

          {/* Proof Requirements Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Proof Requirements
            </h3>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.proofRequired}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    proofRequired: e.target.checked,
                  }))
                }
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Proof of delivery required
              </span>
            </label>

            {formData.proofRequired && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Proof Types
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {proofTypes.map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.requiredProofTypes.includes(type)}
                          onChange={() => handleProofTypeToggle(type)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Proof Count
                  </label>
                  <Input
                    type="number"
                    value={formData.minProofCount}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        minProofCount: parseInt(e.target.value) || 1,
                      }))
                    }
                    min={0}
                    max={20}
                  />
                  {errors.minProofCount && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.minProofCount}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Template Settings Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Settings</h3>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isPublic: e.target.checked }))
                }
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Public (visible to all users)
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.applyOnStageChange}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    applyOnStageChange: e.target.checked,
                  }))
                }
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Auto-apply when lead reaches specific stage
              </span>
            </label>

            {formData.applyOnStageChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Stage *
                </label>
                <select
                  value={formData.targetStage || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      targetStage: e.target.value as Stage,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={formData.applyOnStageChange}
                >
                  <option value="">Select stage...</option>
                  {stages.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
                {errors.targetStage && (
                  <p className="text-sm text-red-600 mt-1">{errors.targetStage}</p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting
                ? (initialData ? 'Updating...' : 'Creating...')
                : initialData
                ? 'Update Template'
                : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
